import { useEffect, useState } from "react";
import { PageLayout } from "@/ui/layouts";
import { Button, Card, Input, Loader, Pagination } from "@/ui/atoms";
import { EmptyState } from "@/ui/molecules";
import type { TAsyncState, TBook } from "@/store";
import { ApiError } from "@/apis/proda_finance";
import { BookOpen, Pencil, Plus, Trash2 } from "lucide-react";

type TBookDraft = {
    number: string;
    name: string;
    is_active: boolean;
};

const createEmptyDraft = (): TBookDraft => ({
    number: "",
    name: "",
    is_active: true,
});

/**
 * View parameters (injected dependencies)
 */
export type TViewBooksParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        useBooksState: () => TAsyncState<TBook[]>;
        onRefresh: () => void;
        onCreateBook: (data: { number: number; name: string; is_active: boolean }) => Promise<void>;
        onUpdateBook: (
            id: string,
            data: { number?: number; name?: string; is_active?: boolean },
        ) => Promise<void>;
        onDeleteBook: (id: string) => Promise<void>;
        onPageChange: (page: number) => void;
        currentPage: () => number;
        pageSize: number;
        total: () => number;
        permissions: () => string[];
    };
};

/**
 * View props (from router/parent)
 */
export type TViewBooksProps = Record<string, never>;

/**
 * Creates the Books view component
 */
export const createViewBooks = ({ useCases }: TViewBooksParams) => {
    const ViewBooks: React.FC<TViewBooksProps> = () => {
        const {
            onMount,
            onUnmount,
            useBooksState,
            onRefresh,
            onCreateBook,
            onUpdateBook,
            onDeleteBook,
            onPageChange,
            currentPage,
            pageSize,
            total,
            permissions,
        } = useCases();

        const perms = permissions();
        const canCreate = perms.includes("settings_book_create");
        const canEdit = perms.includes("settings_book_edit");
        const canDelete = perms.includes("settings_book_delete");

        const booksState = useBooksState();
        const [editingId, setEditingId] = useState<string | null>(null);
        const [editDraft, setEditDraft] = useState<TBookDraft | null>(null);
        const [deletingId, setDeletingId] = useState<string | null>(null);
        const [isAddingNew, setIsAddingNew] = useState(false);
        const [newDraft, setNewDraft] = useState<TBookDraft>(createEmptyDraft);
        const [isSaving, setIsSaving] = useState(false);
        const [saveError, setSaveError] = useState<string | null>(null);
        const [isDuplicate, setIsDuplicate] = useState(false);

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        const isLoading = booksState.status === "pending" && !booksState.data;
        const hasError = booksState.status === "rejected";

        const books = booksState.data ?? [];

        const startEdit = (row: TBook): void => {
            setEditingId(row.id);
            setDeletingId(null);
            setIsAddingNew(false);
            setEditDraft({
                number: String(row.number),
                name: row.name,
                is_active: row.isActive,
            });
        };

        const cancelEdit = (): void => {
            setEditingId(null);
            setEditDraft(null);
        };

        const saveEdit = async (): Promise<void> => {
            if (!editingId || !editDraft) {
                return;
            }
            const num = parseInt(editDraft.number, 10);
            if (isNaN(num) || !editDraft.name.trim()) {
                return;
            }

            setIsSaving(true);
            try {
                await onUpdateBook(editingId, {
                    number: num,
                    name: editDraft.name.trim(),
                    is_active: editDraft.is_active,
                });
                cancelEdit();
            } finally {
                setIsSaving(false);
            }
        };

        const saveNew = async (): Promise<void> => {
            const num = parseInt(newDraft.number, 10);
            if (isNaN(num) || !newDraft.name.trim()) {
                return;
            }

            setIsSaving(true);
            setSaveError(null);
            setIsDuplicate(false);
            try {
                await onCreateBook({
                    number: num,
                    name: newDraft.name.trim(),
                    is_active: newDraft.is_active,
                });
                setIsAddingNew(false);
                setNewDraft(createEmptyDraft());
            } catch (err) {
                setIsDuplicate(err instanceof ApiError && err.status === 409);
                setSaveError(err instanceof Error ? err.message : "Failed to create book");
            } finally {
                setIsSaving(false);
            }
        };

        const cancelNew = (): void => {
            setIsAddingNew(false);
            setNewDraft(createEmptyDraft());
            setSaveError(null);
            setIsDuplicate(false);
        };

        const confirmDelete = async (id: string): Promise<void> => {
            setIsSaving(true);
            try {
                await onDeleteBook(id);
                setDeletingId(null);
            } finally {
                setIsSaving(false);
            }
        };

        if (isLoading) {
            return (
                <PageLayout
                    title="Books"
                    description="Manage financial books"
                    icon={<BookOpen className="h-6 w-6" />}
                >
                    <div className="flex justify-center py-12">
                        <Loader size="lg" />
                    </div>
                </PageLayout>
            );
        }

        if (hasError) {
            return (
                <PageLayout
                    title="Books"
                    description="Manage financial books"
                    icon={<BookOpen className="h-6 w-6" />}
                >
                    <EmptyState
                        variant="error"
                        title="Failed to load books"
                        description="An error occurred while loading book data"
                        action={{ label: "Try Again", onClick: onRefresh }}
                    />
                </PageLayout>
            );
        }

        return (
            <PageLayout
                title="Books"
                description="Manage financial books"
                icon={<BookOpen className="h-6 w-6" />}
                actions={
                    <div className="flex gap-2">
                        {canCreate && (
                            <Button
                                onClick={() => {
                                    setIsAddingNew(true);
                                    setEditingId(null);
                                    setEditDraft(null);
                                    setSaveError(null);
                                    setIsDuplicate(false);
                                }}
                                disabled={isAddingNew || isSaving}
                            >
                                <Plus className="mr-2 h-4 w-4" />
                                Add New
                            </Button>
                        )}
                    </div>
                }
            >
                <Card className="overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b">
                            <tr>
                                <th className="text-left font-semibold px-4 py-3">Number</th>
                                <th className="text-left font-semibold px-4 py-3">Name</th>
                                <th className="text-left font-semibold px-4 py-3">Type</th>
                                <th className="text-left font-semibold px-4 py-3">Status</th>
                                <th className="text-right font-semibold px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {books.map((row) => {
                                const isEditing = editingId === row.id;
                                return (
                                    <tr
                                        key={row.id}
                                        className="border-b last:border-b-0 even:bg-muted/40"
                                    >
                                        <td className="px-4 py-3">
                                            {isEditing && editDraft ? (
                                                <Input
                                                    type="number"
                                                    value={editDraft.number}
                                                    onChange={(event) =>
                                                        setEditDraft({
                                                            ...editDraft,
                                                            number: event.target.value,
                                                        })
                                                    }
                                                />
                                            ) : (
                                                <span className="font-mono font-medium">
                                                    {row.number}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {isEditing && editDraft ? (
                                                <Input
                                                    value={editDraft.name}
                                                    onChange={(event) =>
                                                        setEditDraft({
                                                            ...editDraft,
                                                            name: event.target.value,
                                                        })
                                                    }
                                                />
                                            ) : (
                                                <span>{row.name}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {row.isBudgetBook ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                                    Budget
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                                    Regular
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {isEditing && editDraft ? (
                                                <label className="inline-flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={editDraft.is_active}
                                                        onChange={(event) =>
                                                            setEditDraft({
                                                                ...editDraft,
                                                                is_active: event.target.checked,
                                                            })
                                                        }
                                                    />
                                                    <span>Active</span>
                                                </label>
                                            ) : row.isActive ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                                    Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {isEditing ? (
                                                <div className="inline-flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        onClick={() => {
                                                            void saveEdit();
                                                        }}
                                                        disabled={isSaving}
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={cancelEdit}
                                                        disabled={isSaving}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            ) : deletingId === row.id ? (
                                                <div className="inline-flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="destructive"
                                                        onClick={() => {
                                                            void confirmDelete(row.id);
                                                        }}
                                                        disabled={isSaving}
                                                    >
                                                        Confirm
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setDeletingId(null)}
                                                        disabled={isSaving}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="inline-flex gap-2">
                                                    {canEdit && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => startEdit(row)}
                                                            disabled={isSaving}
                                                        >
                                                            <Pencil className="h-3 w-3 mr-1" />
                                                            Edit
                                                        </Button>
                                                    )}
                                                    {canDelete && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => setDeletingId(row.id)}
                                                            disabled={isSaving}
                                                        >
                                                            <Trash2 className="h-3 w-3 mr-1" />
                                                            Delete
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}

                            {isAddingNew && (
                                <tr className="border-b last:border-b-0 bg-slate-50/60">
                                    <td className="px-4 py-3">
                                        <Input
                                            type="number"
                                            placeholder="Number"
                                            error={isDuplicate}
                                            value={newDraft.number}
                                            onChange={(event) => {
                                                setNewDraft({
                                                    ...newDraft,
                                                    number: event.target.value,
                                                });
                                                if (isDuplicate) {
                                                    setIsDuplicate(false);
                                                    setSaveError(null);
                                                }
                                            }}
                                        />
                                    </td>
                                    <td className="px-4 py-3">
                                        <Input
                                            placeholder="Name"
                                            value={newDraft.name}
                                            onChange={(event) =>
                                                setNewDraft({
                                                    ...newDraft,
                                                    name: event.target.value,
                                                })
                                            }
                                        />
                                    </td>
                                    <td className="px-4 py-3" />
                                    <td className="px-4 py-3">
                                        <label className="inline-flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={newDraft.is_active}
                                                onChange={(event) =>
                                                    setNewDraft({
                                                        ...newDraft,
                                                        is_active: event.target.checked,
                                                    })
                                                }
                                            />
                                            <span>Active</span>
                                        </label>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex gap-2">
                                            <Button
                                                size="sm"
                                                onClick={() => {
                                                    void saveNew();
                                                }}
                                                disabled={isSaving}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={cancelNew}
                                                disabled={isSaving}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {isAddingNew && saveError && (
                                <tr className="bg-red-50">
                                    <td colSpan={5} className="px-4 py-2 text-sm text-red-700">
                                        {saveError}
                                    </td>
                                </tr>
                            )}

                            {books.length === 0 && !isAddingNew && (
                                <tr>
                                    <td
                                        colSpan={5}
                                        className="px-4 py-8 text-center text-sm text-muted-foreground"
                                    >
                                        No books yet. Click &quot;Add New&quot; to create your first
                                        one.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Card>

                <Pagination
                    page={currentPage()}
                    pageSize={pageSize}
                    total={total()}
                    onPageChange={onPageChange}
                    className="mt-4"
                />
            </PageLayout>
        );
    };

    return ViewBooks;
};
